<script setup lang="ts">
/**
 * 兌換碼 — 活動清單（批104）。
 *
 * **兩個 realm 共用這一支**（我方 `/redeem-campaigns`、代理商 `/reseller/redeem-campaigns`），差別只有三處，
 * 全部由 `lib/redeemApi.ts` 的 `mode` 決定：
 *   ① 打哪個 API 前綴；② 代理商看不到「歸屬」欄（他的活動必然都是自家的）與內部備註；
 *   ③ **建立**只有我方 superadmin 看得到（後端代理商 realm 根本沒有那支端點）。
 *
 * ★ 路由 `meta.roles`＋側欄 nav 的 `roles`——後端是 `require_admin` 起跳，前端不藏就是給
 *   operator 一個必然失敗的入口（批80 踩過一次）。
 *
 * 清單與表單的形狀仿 `InviteCodesView.vue`（keyset 清單＋`UiModal`）。
 */
import { computed, onMounted, ref } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import ModulePicker from '@/components/ModulePicker.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import type { PlanSpec, QuotaSpec, RedeemCampaign, RedeemCampaignInput, RedeemEffect } from '@/api/ls'
import * as ls from '@/api/ls'
import { addonQuotaInputAttrs, addonQuotaInputMeta, addonStepError } from '@/lib/addonQuotaInput'
import { redeemKindLabel } from '@/lib/licenseLabels'
import { isInternalPlan } from '@/lib/products'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { quotaRequired, quotaSummaryText } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { useRedeem } from '@/lib/redeemApi'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()
const { mode, api, pr } = useRedeem()

const rows = ref<RedeemCampaign[]>([])
const loading = ref(true)
const loadError = ref('')
/** keyset 續頁游標；null＝已載完，不畫「載入更多」。 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)

// 過濾（狀態兩個 realm 都有；產品／類型只有我方 realm 的端點吃）
// 批186：條件記住。`resFilter` 是批107 的代理商過濾（**活動歸屬**）：`''`＝全部、`'none'`＝我方直營、uuid＝那一家。
const pf = usePersistedFilters(
  'redeem-campaigns',
  { statusFilter: '', kindFilter: '', productFilter: '', resFilter: '' },
  {
    oneOf: {
      statusFilter: ['', 'active', 'paused'],
      kindFilter: ['', 'extend', 'trial', 'grant'],
    },
  },
)
const { statusFilter, kindFilter, productFilter, resFilter } = pf.refs
const { dirty: filtersDirty } = pf
/** 下拉是 `@change` 驅動，程式改值不會觸發，所以清除後要自己重載。 */
const clearFilters = () => {
  pf.clear()
  void reload()
}

/** 建立活動＝**我方 superadmin 專屬**（後端 `require_superadmin`；代理商 realm 沒有這支）。 */
const canCreate = computed(() => mode() === 'admin' && auth.isManagerOrAbove)
const isReseller = computed(() => mode() === 'reseller')

function listOpts(cursor: string | null) {
  return {
    cursor,
    status: statusFilter.value || undefined,
    kind: isReseller.value ? undefined : kindFilter.value || undefined,
    product: isReseller.value ? undefined : productFilter.value || undefined,
    // 代理商 realm 的 api 分流會把這個丟掉（他的活動必然都是自家的）——這裡仍然
    // 不送，免得讀 `listOpts` 的人以為代理商也能過濾別家。
    reseller_id: isReseller.value ? undefined : resFilter.value || undefined,
  }
}

async function loadPage(cursor: string | null) {
  const page = await api().list(listOpts(cursor))
  rows.value = cursor ? [...rows.value, ...page.items] : page.items
  nextCursor.value = page.next_cursor
}

async function reload() {
  loading.value = true
  try {
    await loadPage(null)
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取兌換活動失敗')
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    await loadPage(nextCursor.value)
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(async () => {
  // 產品用來把 key 翻中文名與提供模組／配額／套裝；代理商那邊打的是自己 realm 的那支。
  store.ensureProducts().catch(() => {})
  // 代理商名稱（清單的「歸屬」欄）與建立表單的下拉。★ admin 打 `/resellers` 會 403，
  // `ensureResellers()` 自己吞掉並標記 `resellersDenied` → 「歸屬」欄退化成「代理商專屬」
  // （不印 uuid 前綴，那對操作者沒有意義）。代理商 realm 不需要這一欄，整個不載。
  if (!isReseller.value) store.ensureResellers().catch(() => {})
  await reload()
})

/** 「歸屬」欄：null＝直營；讀不到代理商清單（admin）時只說「代理商專屬」。 */
function ownerText(c: RedeemCampaign): string {
  if (!c.reseller_id) return '直營'
  return store.resellersDenied ? '代理商專屬' : store.resellerName(c.reseller_id)
}

function errText(e: unknown, fallback: string): string {
  const d =
    e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  return obj.message ?? obj.code ?? JSON.stringify(d)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

// --- 顯示 -------------------------------------------------------------------

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}
function windowText(c: RedeemCampaign): string {
  if (!c.starts_at && !c.ends_at) return '不限'
  return `${fmtDate(c.starts_at)} ~ ${fmtDate(c.ends_at)}`
}
/** 名額：unique＝已發放／上限；shared＝已兌換／上限。null＝不限量。 */
function quotaText(c: RedeemCampaign): string {
  const used = c.code_mode === 'unique' ? c.issued_count : c.redeemed_count
  const unit = c.code_mode === 'unique' ? '已發' : '已兌'
  return c.quantity == null ? `${unit} ${used}／不限` : `${unit} ${used}／${c.quantity}`
}
function effectText(c: RedeemCampaign): string {
  const s = c.effect_summary
  // 批108：期限可以短到分鐘，光印天數會讓「30 分鐘」的效果顯示成「0 天」。
  // `term_text` 是後端 `effect_summary` 算好的（`days * 1440 + minutes` → 人話）。
  const days = s.term_text || (s.days ? `${s.days} 天` : '—')
  if (c.kind === 'extend') return `延長 ${days}`
  const mods = (s.modules ?? []).length ? `${(s.modules ?? []).length} 個模組` : '不含模組'
  if (c.kind === 'trial') return `${mods}・${days}`
  // 批114 裁示 ⑰：效果的數量改用憑證設計的標籤組字（批114 之前建的活動把數字存在
  // effect 的一級鍵上，`seedQuotas` 會把它併進來）。
  const specOf = store.productByKey(c.product)
  const quotaText = quotaSummaryText(specOf, seedQuotas(specOf, s))
  return `${s.plan_label || '試用授權'}・${mods}${quotaText ? `・${quotaText}` : ''}・${days}`
}
function statusLabel(s: string): string {
  return { active: '進行中', paused: '已暫停' }[s] ?? s
}
function codeModeLabel(m: string): string {
  return { shared: '共用碼', unique: '不重複子碼' }[m] ?? m
}

// --- 建立 -------------------------------------------------------------------

interface Draft {
  name: string
  product: string
  kind: 'extend' | 'trial' | 'grant'
  codeMode: 'shared' | 'unique'
  sharedCode: string
  codePrefix: string
  resellerId: string
  days: number
  /** 批108：分鐘餘數（與 days 相加＝總長度）。 */
  minutes: number
  label: string
  planLabel: string
  quantity: string
  unlimited: boolean
  startsAt: string
  endsAt: string
  stackable: boolean
  /** 批107：`deployment`＝每台部署一次｜`customer`＝每個客戶一次。 */
  oncePer: string
  targetLicense: string
  eligiblePlans: string[]
  notes: string
}

function blankDraft(): Draft {
  return {
    name: '',
    product: store.products.find((p) => p.status === 'active')?.key ?? '',
    kind: 'extend',
    codeMode: 'shared',
    sharedCode: '',
    codePrefix: '',
    resellerId: '',
    days: 30,
    minutes: 0,
    label: '',
    planLabel: '',
    quantity: '',
    unlimited: true,
    startsAt: '',
    endsAt: '',
    stackable: false,
    // ★ 預設「每客戶一次」（主人裁示，與後端 `CampaignIn.once_per` 的預設一致）
    //   ——要讓多站客戶每站都領再改成每部署一次。
    oncePer: 'customer',
    targetLicense: 'any',
    eligiblePlans: [],
    notes: '',
  }
}

const createOpen = ref(false)
const creating = ref(false)
const draft = ref<Draft>(blankDraft())
const modules = ref<Record<string, string>>({})
/**
 * 逐配額鍵的「要不要給」＋輸入值（同 `AddonIssueModal`：沒勾＝鍵不存在，不是給 0）。
 * grant（全新試用主證）填的是新證上的值；trial（子證）填什麼依該鍵宣告的子證合併方式
 * （批151 裁示 98：`sum` 填增加量，其餘填目標值，見 `lib/addonQuotaInput.ts`）。
 */
const quotaOn = ref<Record<string, boolean>>({})
const quotaTo = ref<Record<string, string>>({})
/** ModulePicker／配額列的重掛鍵——換產品或套用套裝時 bump（那支只在掛載時 initFrom）。 */
const formKey = ref(0)
/** 內容帶入用的套裝（trial＝加購套裝；grant＝可販售／試用套裝）。 */
const presetKey = ref('')

const spec = computed(() => store.productByKey(draft.value.product))
const activeProducts = computed(() => store.products.filter((p) => p.status === 'active'))
const quotaFields = computed(() => spec.value?.quotas ?? [])
// 批155：兌換碼是給客戶用的（外部路徑）——內部套裝一律不列。
const presetPlans = computed<PlanSpec[]>(() => {
  const plans = (spec.value?.plans ?? []).filter((p) => p.is_active && !isInternalPlan(p))
  return draft.value.kind === 'trial'
    ? plans.filter((p) => p.kind === 'addon')
    : plans.filter((p) => p.kind === 'plan' || p.kind === 'trial')
})
const sellablePlans = computed<PlanSpec[]>(() =>
  (spec.value?.plans ?? []).filter((p) => p.is_active && p.kind === 'plan' && !isInternalPlan(p)),
)
const activeResellers = computed(() => store.resellers.filter((r) => r.active))

const TERM_CHOICES = [7, 14, 30, 90, 180, 365]

function openCreate() {
  draft.value = blankDraft()
  modules.value = {}
  quotaOn.value = {}
  quotaTo.value = {}
  presetKey.value = ''
  formKey.value++
  createOpen.value = true
}

/** 換產品：模組／配額鍵整組不同，沿用舊狀態會把上一個產品的勾選帶進新活動。 */
function onProductChange() {
  modules.value = {}
  quotaOn.value = {}
  quotaTo.value = {}
  presetKey.value = ''
  draft.value.eligiblePlans = []
  formKey.value++
}

/** 換類型：效果欄位整段換掉，套裝帶入的來源也不同 → 一併清空。 */
function onKindChange() {
  presetKey.value = ''
  modules.value = {}
  quotaOn.value = {}
  quotaTo.value = {}
  formKey.value++
}

/**
 * 從既有套裝「帶入當起點」（裁示 20）——**存的是活動自己的副本**，不是套裝鍵。
 * 之後套裝改了內容，已建的活動不受影響（客戶兌到的東西必須與當初宣稱的一致）。
 */
function applyPreset() {
  const plan = presetPlans.value.find((p) => p.key === presetKey.value)
  if (!plan) return
  modules.value = { ...plan.modules }
  quotaOn.value = {}
  quotaTo.value = {}
  // 批114：模板的相容數字欄先併進配額包再勾選（只讀 `quotas` 會讓那些數字消失——
  // 少一個鍵就是那一項完全不給；trial 的子證依各鍵宣告的合併方式與主證合併）。
  for (const [k, v] of Object.entries(seedQuotas(spec.value, plan) ?? {})) {
    if (v == null) continue
    quotaOn.value[k] = true
    quotaTo.value[k] = String(v)
  }
  if (draft.value.kind === 'trial') draft.value.label = plan.label
  else draft.value.planLabel = plan.label
  if (plan.term_days) draft.value.days = plan.term_days
  formKey.value++
}

/**
 * grant（發一張全新試用主證）還缺哪些**必填**配額（批114）。
 *
 * 憑證設計把「這個鍵簽發時必須有值」做成旗標（`QuotaSpec.required`），後端簽發時照它擋
 * 422；活動建立當下就先講，才不會等到客戶兌換的那一刻才失敗。
 * ★ `extend`／`trial` 不吃這條：那兩種是「加在既有證上」的增益，缺鍵＝不動那一項。
 */
const missingRequiredQuotas = computed<string[]>(() =>
  quotaFields.value
    .filter((f) => quotaRequired(f) && !f.deprecated && !(quotaPayload.value ?? {})[f.key])
    .map((f) => f.label),
)

/** 配額輸入框的 placeholder：trial（子證）依合併方式（加總＝增加量），grant（新證）一律目標值。 */
function quotaInputLabel(f: QuotaSpec): string {
  return draft.value.kind === 'trial' ? addonQuotaInputMeta(f).inputLabel : '目標值'
}

/** 配額輸入框的 `min`／`step`：trial（子證）照加購級距，grant（新證）不受級距限制。 */
function quotaInputAttrs(f: QuotaSpec): { min: number; step: number } {
  return draft.value.kind === 'trial' ? addonQuotaInputAttrs(f) : { min: 1, step: 1 }
}

/**
 * 加購數量級距（11-01 裁示）：trial 效果兌換時簽的是**子證**，勾了、填了值卻不是級距的倍數 →
 * 那一列紅字、擋建立（後端建立／修改活動時同樣 422 `addon_quota_step`）。grant 簽的是主證，不受級距限制。
 */
const quotaStepIssues = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  if (draft.value.kind !== 'trial') return out
  for (const f of quotaFields.value) {
    if (!quotaOn.value[f.key]) continue
    // 空白與 0 不在這一關擋（`quotaPayload` 本來就不送它們）。
    const msg = addonStepError(f, quotaTo.value[f.key], { allowUnset: true })
    if (msg) out[f.key] = msg
  }
  return out
})

const quotaPayload = computed<Record<string, number> | null>(() => {
  const out: Record<string, number> = {}
  for (const f of quotaFields.value) {
    if (!quotaOn.value[f.key]) continue
    const n = Number(quotaTo.value[f.key])
    if (Number.isFinite(n) && n > 0) out[f.key] = Math.floor(n)
  }
  return Object.keys(out).length ? out : null
})

/** `YYYY-MM-DD` → ISO。`end` 取當地時間的當日尾巴（活動「到這一天為止」含當天）。 */
function toIso(day: string, end = false): string | null {
  if (!day) return null
  const d = new Date(`${day}T${end ? '23:59:59' : '00:00:00'}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function buildEffect(): RedeemEffect {
  // 批108：效果長度＝`days * 1440 + minutes`（兩個鍵都在 JSONB 的 effect 裡，零 migration）。
  const eff: RedeemEffect = {
    days: Number(draft.value.days) || 0,
    minutes: Number(draft.value.minutes) || 0,
  }
  if (draft.value.kind === 'extend') return eff
  if (draft.value.kind === 'trial' && draft.value.label.trim()) eff.label = draft.value.label.trim()
  if (draft.value.kind === 'grant') eff.plan_label = draft.value.planLabel.trim() || '兌換試用'
  if (Object.keys(modules.value).length) eff.modules = modules.value
  // 批114：數量只送 `quotas`（effect 的一級數字鍵仍是相容輸入，後端會折進來，但後台
  // 不再送——同一個數字有兩個輸入框，而且獨立欄會靜默蓋掉配額表裡填的值）。
  if (quotaPayload.value) eff.quotas = quotaPayload.value
  return eff
}

const canSubmit = computed(() => {
  const d = draft.value
  if (creating.value) return false
  if (!d.name.trim() || !d.product) return false
  // 批108：天與分鐘可以擇一或並用，判準是**總長度**（「0 天 30 分鐘」是合法效果）。
  if (!(Number(d.days) * 1440 + Number(d.minutes) > 0)) return false
  if (d.kind === 'grant') {
    // grant＝一整張新證：模組與**必填配額**是新證的骨架，後端也硬擋（`bad_effect`／
    // 422 `quotas_invalid`）。★ 批114：哪些配額必填由憑證設計宣告（`QuotaSpec.required`），
    // 這裡不再寫死任何一個鍵。
    if (!Object.keys(modules.value).length) return false
    if (missingRequiredQuotas.value.length) return false
  }
  if (d.kind === 'trial' && !Object.keys(modules.value).length && !quotaPayload.value) return false
  if (Object.keys(quotaStepIssues.value).length) return false
  return true
})

async function doCreate() {
  if (!canSubmit.value) return
  const d = draft.value
  const body: RedeemCampaignInput = {
    name: d.name.trim(),
    product: d.product,
    kind: d.kind,
    effect: buildEffect(),
    code_mode: d.codeMode,
    notes: d.notes.trim() || null,
    reseller_id: d.resellerId || null,
    code_prefix: d.codeMode === 'unique' ? d.codePrefix.trim() || null : null,
    shared_code: d.codeMode === 'shared' ? d.sharedCode.trim() || null : null,
    quantity: d.unlimited ? null : Number(d.quantity) > 0 ? Math.floor(Number(d.quantity)) : null,
    starts_at: toIso(d.startsAt),
    ends_at: toIso(d.endsAt, true),
    stackable: d.stackable,
    once_per: d.oncePer,
    // grant 沒有「既有授權」可以比對，這兩項後端也不看（裁示 20）。
    target_license: d.kind === 'grant' ? 'any' : d.targetLicense,
    eligible_plans: d.kind === 'grant' || !d.eligiblePlans.length ? null : d.eligiblePlans,
  }
  creating.value = true
  try {
    const created = await ls.createRedeemCampaign(body)
    createOpen.value = false
    await reload()
    if (created.shared_code) {
      await dialogs.notify({
        title: '活動已建立',
        message: `共用代碼：${created.shared_code.display_code}\n請到活動詳情頁複製給客戶。`,
      })
    }
  } catch (e) {
    await fail(e, '建立失敗')
  } finally {
    creating.value = false
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>兌換碼</h1>
      <div class="head-acts">
        <select v-model="statusFilter" class="f-sel" @change="reload()">
          <option value="">全部狀態</option>
          <option value="active">進行中</option>
          <option value="paused">已暫停</option>
        </select>
        <select v-if="!isReseller" v-model="kindFilter" class="f-sel" @change="reload()">
          <option value="">全部類型</option>
          <option value="extend">延長期限</option>
          <option value="trial">功能試用</option>
          <option value="grant">領取試用授權</option>
        </select>
        <select v-if="!isReseller" v-model="resFilter" class="f-sel" @change="reload()">
          <option value="">全部歸屬</option>
          <option value="none">我方直營</option>
          <option v-for="r in store.resellers" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
        <select v-if="!isReseller" v-model="productFilter" class="f-sel" @change="reload()">
          <option value="">全部產品</option>
          <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
        <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
        <UiButton v-if="canCreate" variant="primary" @click="openCreate">新增活動</UiButton>
      </div>
    </div>

    <div class="card">
      <p class="subtle">
        <template v-if="isReseller">
          貴公司專屬的兌換活動。活動內容由愛+1 建立，您可以在活動詳情頁<strong>發放子碼</strong>、
          作廢尚未兌換的子碼，並查看誰兌換了、用在哪一台。
        </template>
        <template v-else>
          兌換碼分三種：<strong>延長期限</strong>（重簽主憑證）、<strong>功能試用</strong>（簽一張試用子憑證）、
          <strong>領取試用授權</strong>（尚未取得授權的站台連結後兌換，換一張全新試用主證）。
          建立與修改是超級管理者；發放與作廢 admin 即可。
        </template>
      </p>

      <p v-if="loadError" class="err-line">{{ loadError }}</p>
      <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>活動</th>
              <th>類型 / 效果</th>
              <th v-if="!isReseller">產品</th>
              <th v-if="!isReseller">歸屬</th>
              <th>代碼形態</th>
              <th>名額</th>
              <th>兌換窗口</th>
              <th>已兌換</th>
              <th>狀態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in rows" :key="c.id">
              <td>
                <RouterLink class="lnk" :to="{ name: pr('redeem-campaign-detail'), params: { id: c.id } }">
                  {{ c.name }}
                </RouterLink>
                <div v-if="c.notes" class="sub">{{ c.notes }}</div>
              </td>
              <td>
                <span class="status-pill" :class="c.kind === 'grant' ? 'pending' : 'muted'">
                  {{ redeemKindLabel(c.kind) }}
                </span>
                <div class="sub">{{ effectText(c) }}</div>
              </td>
              <td v-if="!isReseller" class="small">{{ store.productName(c.product) }}</td>
              <td v-if="!isReseller" class="small">{{ ownerText(c) }}</td>
              <td class="small">
                {{ codeModeLabel(c.code_mode) }}
                <div v-if="c.code_prefix" class="sub mono">{{ c.code_prefix }}-</div>
              </td>
              <td class="small nw-c">{{ quotaText(c) }}</td>
              <td class="small nw-c">{{ windowText(c) }}</td>
              <td class="small">
                {{ c.redeemed_count }}
                <div v-if="c.stackable" class="sub">可併用</div>
              </td>
              <td>
                <span class="status-pill" :class="c.status === 'active' ? 'active' : 'revoked'">
                  {{ statusLabel(c.status) }}
                </span>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <RouterLink
                    class="btn secondary sm"
                    :to="{ name: pr('redeem-campaign-detail'), params: { id: c.id } }"
                  >
                    管理
                  </RouterLink>
                </div>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="isReseller ? 8 : 10" class="subtle empty-row">
                目前沒有符合條件的兌換活動。
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。 -->
      <div v-if="nextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已載入 {{ rows.length }} 檔</span>
      </div>
    </div>

    <!-- 建立活動（superadmin） -->
    <UiModal :open="createOpen" title="新增兌換活動" width="880px" @close="createOpen = false">
      <p class="pm-note">
        <strong>產品、類型、代碼形態、代理商歸屬、子碼前綴建立後不可修改</strong>
        （它們會被發出去的代碼與簽出去的憑證實體化）；效果內容在<strong>第一次兌換之後</strong>鎖定。
      </p>

      <div class="pm-grid">
        <label class="field">
          <span class="field-label">活動名稱（客戶兌換時看得到）</span>
          <input v-model="draft.name" maxlength="120" placeholder="2026 春季展 延長 30 天" />
        </label>
        <label class="field">
          <span class="field-label">產品</span>
          <select v-model="draft.product" @change="onProductChange()">
            <option v-for="p in activeProducts" :key="p.key" :value="p.key">{{ p.name }}</option>
          </select>
        </label>
      </div>

      <fieldset class="pm-box">
        <legend>類型</legend>
        <div class="pm-radios">
          <label><input v-model="draft.kind" type="radio" value="extend" @change="onKindChange()" /> 延長期限</label>
          <label><input v-model="draft.kind" type="radio" value="trial" @change="onKindChange()" /> 功能試用</label>
          <label><input v-model="draft.kind" type="radio" value="grant" @change="onKindChange()" /> 領取試用授權</label>
        </div>
        <p class="pm-hint">
          <template v-if="draft.kind === 'extend'">
            重簽主憑證、把到期日往後推。已到期的站台從今天起算——這是救回唯讀站的主要途徑。
          </template>
          <template v-else-if="draft.kind === 'trial'">
            簽一張<strong>試用子憑證</strong>加在現行授權之上。內容已被主憑證涵蓋時兌換會被拒
            （不浪費名額）；功能目前來自別張子證時，效果是接著它往後延長。
          </template>
          <template v-else>
            給<strong>還沒有任何授權</strong>的站台換一張全新的試用主證。客戶要先在產品內
            〈以 Google 連結 License Server〉才兌換得了（portal 與外部產品不開放這一種）。
          </template>
        </p>
      </fieldset>

      <!-- 效果內容：三種類型三段 -->
      <fieldset class="pm-box">
        <legend>效果內容</legend>

        <div v-if="draft.kind !== 'extend'" class="pm-grid">
          <label class="field">
            <span class="field-label">從既有套裝帶入（只是起點，存的是活動自己的副本）</span>
            <select v-model="presetKey" @change="applyPreset()">
              <option value="">（不帶入）</option>
              <option v-for="p in presetPlans" :key="p.key" :value="p.key">{{ p.label }}</option>
            </select>
          </label>
          <label v-if="draft.kind === 'trial'" class="field">
            <span class="field-label">子憑證名稱（客戶在授權頁看得到）</span>
            <input v-model="draft.label" maxlength="120" placeholder="AI 自動客服試用" />
          </label>
          <label v-else class="field">
            <span class="field-label">授權顯示名稱</span>
            <input v-model="draft.planLabel" maxlength="120" placeholder="展會試用版" />
          </label>
        </div>

        <div class="pm-term">
          <span class="pm-term-label">天數</span>
          <button
            v-for="d in TERM_CHOICES"
            :key="d"
            type="button"
            class="chip pick"
            :class="{ on: draft.days === d }"
            @click="draft.days = d"
          >
            {{ d }} 天
          </button>
          <input v-model.number="draft.days" type="number" min="0" class="pm-num" />
          <!-- 批108：分鐘欄。展會／教育訓練要發「再體驗 30 分鐘」的碼就靠它；
               與天數相加＝總長度（兩個都填 0 才會被擋）。 -->
          <span class="pm-term-label">分鐘</span>
          <input v-model.number="draft.minutes" type="number" min="0" max="1439" class="pm-num" />
        </div>

        <template v-if="draft.kind !== 'extend'">
          <ModulePicker :key="`mods-${formKey}`" v-model="modules" :spec="spec" />

          <!-- 批114：獨立的數量欄已拔除——數量一律在這張配額表裡勾（同一個數字本來有
               兩個輸入框，而且獨立欄會靜默蓋掉這裡填的值）。 -->
          <div v-if="quotaFields.length" class="pm-quotas" :key="`quota-${formKey}`">
            <div v-if="draft.kind === 'trial'" class="pm-sub">
              配額（依各項的合併方式填寫：標「加總」的填<strong>增加量</strong>，其餘填目標值；沒勾的項目完全不受影響）
            </div>
            <div v-else class="pm-sub">配額（新授權上的<strong>絕對值</strong>；沒勾的項目完全不受影響）</div>
            <div class="pm-qgrid">
              <div v-for="f in quotaFields" :key="f.key" class="pm-q" :class="{ dim: f.deprecated }">
                <label class="pm-q-on">
                  <input v-model="quotaOn[f.key]" type="checkbox" />
                  <span :title="f.hint ?? f.key">{{ f.label }}</span>
                </label>
                <input
                  v-model="quotaTo[f.key]"
                  type="number"
                  :min="quotaInputAttrs(f).min"
                  :step="quotaInputAttrs(f).step"
                  class="pm-num"
                  :class="{ bad: !!quotaStepIssues[f.key] }"
                  :disabled="!quotaOn[f.key]"
                  :placeholder="quotaInputLabel(f)"
                />
                <small v-if="draft.kind === 'trial' && addonQuotaInputMeta(f).note" class="pm-q-merge">
                  {{ addonQuotaInputMeta(f).note }}
                </small>
                <small v-if="quotaStepIssues[f.key]" class="pm-q-step">{{ quotaStepIssues[f.key] }}</small>
              </div>
            </div>
            <!-- grant＝發一張全新的證：憑證設計標了「必填」的配額一定要給值，否則客戶
                 兌換的當下才會失敗（後端 422）。 -->
            <p v-if="draft.kind === 'grant' && missingRequiredQuotas.length" class="pm-hint err">
              ⚠ 這個產品的「{{ missingRequiredQuotas.join('、') }}」是必填配額，請勾選並填上數值。
            </p>
          </div>
        </template>
      </fieldset>

      <fieldset class="pm-box">
        <legend>代碼形態</legend>
        <div class="pm-radios">
          <label><input v-model="draft.codeMode" type="radio" value="shared" /> 共用碼（一組碼大家共用）</label>
          <label><input v-model="draft.codeMode" type="radio" value="unique" /> 不重複子碼（發放時才產生）</label>
        </div>
        <div class="pm-grid">
          <label v-if="draft.codeMode === 'shared'" class="field">
            <span class="field-label">自訂共用碼（留空＝自動產生）</span>
            <input v-model="draft.sharedCode" class="mono" maxlength="32" placeholder="SPRING26" />
            <small class="pm-hint">8–32 碼，只能是大寫英文與數字。</small>
          </label>
          <label v-else class="field">
            <span class="field-label">子碼前綴（選填）</span>
            <input v-model="draft.codePrefix" class="mono" maxlength="12" placeholder="EXPO26" />
            <small class="pm-hint">
              有前綴時子碼長成 <code class="mono">EXPO26-XXXX-XXXX-XXXX</code>；隨機部分仍是 12 碼。
            </small>
          </label>
          <label class="field">
            <span class="field-label">
              限量（{{ draft.codeMode === 'unique' ? '可發放的子碼總數' : '兌換次數上限' }}）
            </span>
            <div class="pm-inline">
              <input
                v-model="draft.quantity"
                type="number"
                min="1"
                class="pm-num"
                :disabled="draft.unlimited"
                placeholder="100"
              />
              <label class="pm-chk">
                <input v-model="draft.unlimited" type="checkbox" />
                <span>不限量</span>
              </label>
            </div>
          </label>
        </div>
      </fieldset>

      <fieldset class="pm-box">
        <legend>兌換條件</legend>
        <div class="pm-grid">
          <label class="field">
            <span class="field-label">開始（留空＝立即生效）</span>
            <input v-model="draft.startsAt" type="date" />
          </label>
          <label class="field">
            <span class="field-label">結束（含當天，留空＝不限）</span>
            <input v-model="draft.endsAt" type="date" />
          </label>
        </div>
        <div class="pm-grid">
          <label v-if="canCreate" class="field">
            <span class="field-label">代理商專屬（留空＝我方自營）</span>
            <select v-model="draft.resellerId">
              <option value="">（不限／我方自營）</option>
              <option v-for="r in activeResellers" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <small class="pm-hint">
              指定之後<strong>只有該代理商名下的客戶</strong>兌換得了；沒有代理商的客戶也會被拒。
            </small>
          </label>
          <label v-if="draft.kind !== 'grant'" class="field">
            <span class="field-label">適用對象</span>
            <select v-model="draft.targetLicense">
              <option value="any">不限</option>
              <option value="trial">只限試用授權</option>
              <option value="paid">只限正式授權</option>
            </select>
          </label>
        </div>
        <div v-if="draft.kind !== 'grant' && sellablePlans.length" class="pm-plans">
          <div class="pm-sub">適用套裝（都不勾＝不限）</div>
          <div class="pm-plan-list">
            <label v-for="p in sellablePlans" :key="p.key" class="pm-plan">
              <input v-model="draft.eligiblePlans" type="checkbox" :value="p.key" />
              <span>{{ p.label }}</span>
            </label>
          </div>
        </div>
        <!-- 批107：使用條件。放在併用旗標旁邊——兩者都是「這檔活動怎麼被兌」的規則。 -->
        <div class="pm-row mt">
          <span class="pm-label">使用條件</span>
          <label class="pm-chk">
            <input v-model="draft.oncePer" type="radio" value="customer" />
            <span>單一客戶一次（同組織不論幾台部署）</span>
          </label>
          <label class="pm-chk">
            <input v-model="draft.oncePer" type="radio" value="deployment" />
            <span>單一部署一次（每台各可兌一次）</span>
          </label>
        </div>
        <label class="pm-chk mt">
          <input v-model="draft.stackable" type="checkbox" />
          <span>可與同類型兌換碼併用（不勾＝該部署兌換過同類型兌換碼就拒絕）</span>
        </label>
      </fieldset>

      <label class="field">
        <span class="field-label">內部備註（不會下發到客戶站台）</span>
        <textarea v-model="draft.notes" rows="2" placeholder="給自己人看的備註（選填）" />
      </label>

      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!canSubmit" :loading="creating" @click="doCreate">
          建立活動
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.head-acts {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.f-sel {
  min-width: 130px;
}
.err-line {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.lnk {
  color: var(--brand-600);
  text-decoration: none;
  font-weight: 600;
}
/* 窄畫面下活動名不要被擠成一字一行（400px 實測）。表格本來就會橫向捲動，
   給第一欄一個下限比讓標題直書好讀得多——既有頁面的通病，新頁面不跟進。 */
.tbl td:first-child {
  min-width: 160px;
}
.lnk:hover {
  text-decoration: underline;
}
.sub {
  color: var(--fg-4);
  font-size: var(--fs-11);
  margin-top: 2px;
}
.small {
  font-size: var(--fs-12);
}
.status-pill.muted {
  background: var(--bg);
  color: var(--fg-3);
}
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.mono {
  font-family: ui-monospace, monospace;
  letter-spacing: 1px;
}

/* --- 建立表單 --- */
.pm-note {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-12);
  color: var(--fg-3);
  line-height: var(--lh-base);
}
.pm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
  margin-bottom: var(--sp-3);
}
.pm-box {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
}
.pm-box legend {
  padding: 0 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  font-weight: 600;
}
.pm-radios {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-4);
  font-size: var(--fs-13);
  color: var(--fg-2);
  margin-bottom: var(--sp-2);
}
.pm-radios label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}
.pm-hint {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
/* 必填配額還沒填（批114）：同一個提示位置，改用警示色。 */
.pm-hint.err {
  color: var(--danger);
}
.pm-term {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: var(--sp-3);
}
.pm-term-label {
  font-size: var(--fs-12);
  color: var(--fg-2);
  margin-right: var(--sp-2);
}
.chip.pick {
  cursor: pointer;
  border: 1px solid var(--border-strong);
  background: transparent;
  color: var(--fg-3);
  height: 24px;
}
.chip.pick.on {
  border-color: var(--brand-400);
  background: var(--brand-50);
  color: var(--brand-700);
  font-weight: 600;
}
.pm-num {
  width: 96px;
  text-align: right;
}
.pm-quotas {
  margin: var(--sp-3) 0;
}
.pm-sub {
  font-size: var(--fs-12);
  color: var(--fg-3);
  margin-bottom: 6px;
}
.pm-qgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--sp-2) var(--sp-4);
}
.pm-q {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
}
/* 批151：trial 子證的非預設合併方式說明，獨佔一行放在該列底下。 */
.pm-q-merge {
  flex-basis: 100%;
  margin-top: -4px;
  color: var(--fg-4);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
/* 加購數量級距不合（只有 trial）：同上獨佔一行，紅字。 */
.pm-q-step {
  flex-basis: 100%;
  margin-top: -4px;
  color: var(--danger);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.pm-num.bad {
  border-color: var(--danger);
}
.pm-q.dim {
  opacity: 0.6;
}
.pm-q-on {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  color: var(--fg-2);
  cursor: pointer;
  white-space: nowrap;
}
.pm-inline {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}
.pm-chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  cursor: pointer;
}
.pm-chk.mt {
  margin-top: var(--sp-2);
}
/* 批107：使用條件那一列（標籤＋兩個 radio 並排；窄畫面自動換行）。 */
.pm-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
}
.pm-row.mt {
  margin-top: var(--sp-3);
}
.pm-label {
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--fg);
}
.pm-plans {
  margin-top: var(--sp-2);
}
.pm-plan-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px var(--sp-3);
}
.pm-plan {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  cursor: pointer;
}
@media (max-width: 700px) {
  .pm-grid {
    grid-template-columns: 1fr;
  }
  .page-head {
    flex-direction: column;
    align-items: stretch;
  }
}
/* 手機／平板（批171）：名額（「已兌 1／不限」）與兌換窗口很短，不折行——折了之後每列高一倍。 */
@media (--compact) {
  .nw-c {
    white-space: nowrap;
  }
}
</style>
